import Autolinker from 'autolinker';
import DOMPurify from 'dompurify';
import parse, { DOMNode, domToReact, Element } from 'html-react-parser';
import { ReactElement, ReactNode } from 'react';

import HoverText from '@components/HoverText';
import Link from '@components/text/Link';
import { GetMeQuery } from '@typing/Generated';
import { replaceAll } from '@utils/stringUtils';

type MyExpert = Extract<GetMeQuery['me'], { __typename: 'Expert' }>;

export const sanitizeHtml = (
  rawHtml: string | null | undefined,
  journeyId: string | null,
  myExpert: MyExpert | null | undefined,
  onAppDomainLinkClick?: () => void
) => {
  if (rawHtml) {
    // Strip out unwelcome tags.
    const sanitized = DOMPurify.sanitize(rawHtml);

    const replaced = journeyId ? replaceAll(sanitized, 'JOURNEY_ID', journeyId) : sanitized;

    const handleReplacement = (domNode: DOMNode) => {
      if (domNode instanceof Element && domNode.name === 'a') {
        const href = domNode.attribs.href;
        const appDomain = import.meta.env.VITE_APP_HOST;

        // If the anchor tag is on the current domain, we want to return a router Link so that it uses the
        // router to render only what we need instead of re-rendering the whole page. If it's on a different
        // domain, add the attributes to make it open in a separate tab.
        if (href?.includes(appDomain)) {
          const hrefWithRoute = myExpert ? href.replace('/client/', '/expert/') : href;
          return (
            <Link onClick={onAppDomainLinkClick} routerLink={hrefWithRoute.replace(appDomain, '')}>
              {domToReact(domNode.children)}
            </Link>
          );
        } else {
          domNode.attribs.target = '_blank';
          domNode.attribs.rel = 'noopener noreferrer';
        }
      }

      return domNode;
    };

    // Then autolink urls that are not already in anchor tags and produce an element to inject into the DOM.
    return parse(Autolinker.link(replaced), { replace: handleReplacement });
  }
  return '';
};

export const maybeWrapElementInHoverText = (hoverContent: ReactNode, element: ReactElement) => {
  if (hoverContent) {
    return <HoverText content={hoverContent}>{element}</HoverText>;
  }
  return element;
};
